/**
 * PreDepartureCompactCard.js
 *
 * FT-161 Phase 2: Compact pre-departure checks card for ACTIVE voyage log
 * Renders when ALL pre-departure checks are completed, collapsing 6+ entries into one card.
 *
 * Features:
 * - Shows all checks in a compact list with green checkmarks
 * - Collapsible check list (default expanded)
 * - Note icon on each check to view/add notes
 * - Pre-departure notes from voyage form shown at bottom
 * - "Completed by" footer
 */

import React, { useState } from 'react';
import {
  ClipboardCheck,
  Check,
  ChevronDown,
  ChevronUp,
  MessageSquare
} from 'lucide-react';
import { getOperationTemplate } from '../../../../../config/operationsConfig';
import { useStorageState } from '../../../../../hooks/useStorageState';

// Check if a string looks like a generated ID (timestamp + random chars)
// e.g., "1776161743893-FO0LXY7G5" or "abc123xyz"
const looksLikeGeneratedId = (str) => {
  if (typeof str !== 'string') return false;
  // Pattern: starts with many digits (timestamp) followed by random chars
  if (/^\d{10,}-[A-Z0-9]+$/i.test(str)) return true;
  // Pattern: long string with mix of numbers and random chars, no spaces
  if (str.length > 15 && !/\s/.test(str) && /\d/.test(str) && /[A-Z]/i.test(str)) return true;
  return false;
};

const PreDepartureCompactCard = ({
  checks = [],
  voyage,
  timestamp,
  onAnnotate
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Load voyage settings to look up field labels from template
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Get pre_departure template to look up field labels
  const template = getOperationTemplate('pre_departure', voyageSettings);
  const templateFields = template?.fields || [];

  // Format time
  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // Count checks
  const totalChecks = checks.length;
  const completedChecks = checks.filter(c => c.completed).length;

  // Get completed by (from last check or voyage master)
  const lastCompletedCheck = checks.filter(c => c.completed && c.completedBy).pop();
  const completedBy = lastCompletedCheck?.completedBy || voyage?.masterName || 'Master';

  // Get pre-departure notes from voyage (from StartVoyageFullScreen form)
  const preDepartureNotes = voyage?.preDepartureNotes || voyage?.notes;

  // FT-202: Get custom data fields from voyage settings
  const preDepartureFields = voyage?.preDepartureFields || {};
  const hasCustomFields = Object.keys(preDepartureFields).length > 0;

  // FT-202 FIX: Look up field label from template, fallback to formatting the key
  // Same pattern as OperationCard.js
  const getFieldLabel = (fieldId) => {
    // First try to find the field in the template
    const templateField = templateFields.find(f => f.id === fieldId);
    if (templateField?.label) {
      // Found a template label - but verify it's not an ID-like string
      if (!looksLikeGeneratedId(templateField.label)) {
        return templateField.label;
      }
    }
    // Fallback: format the field ID itself
    // But only if the fieldId itself doesn't look like a generated ID
    if (looksLikeGeneratedId(fieldId)) {
      return null; // Signal to skip this field
    }
    return fieldId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get the timestamp from the last completed check
  const displayTime = timestamp ||
    checks.filter(c => c.timestamp).sort((a, b) =>
      new Date(b.timestamp) - new Date(a.timestamp)
    )[0]?.timestamp;

  // Check if a specific check has a linked note
  const hasNote = (check) => {
    return check.note || check.notes || check.hasNote;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-cyan-600 p-4 mx-4 my-2">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-green-500" />
          <span className="text-base font-semibold text-gray-900">
            Pre-Departure Checks ({completedChecks}/{totalChecks})
          </span>
        </div>
        <span className="text-sm text-gray-400 font-mono">{formatTime(displayTime)}</span>
      </div>

      {/* Collapsible Check List */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full py-2 text-left border-t border-gray-100 mt-2"
      >
        <span className="text-sm font-medium text-gray-600">
          {isExpanded ? 'Hide checks' : 'Show checks'}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Check List */}
      {isExpanded && (
        <div className="space-y-1.5 mt-2">
          {checks.map((check, index) => (
            <div
              key={check.id || index}
              className="flex items-center justify-between py-1"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 truncate">
                  {check.label || check.text || check.name}
                </span>
              </div>
              {/* Note icon */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAnnotate?.(check);
                }}
                className={`p-1 rounded hover:bg-gray-100 flex-shrink-0 ${
                  hasNote(check) ? 'text-cyan-600' : 'text-gray-300 hover:text-cyan-600'
                }`}
                title={hasNote(check) ? 'View/edit note' : 'Add note'}
              >
                <MessageSquare className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* FT-202: Custom Data Fields from Voyage Settings */}
      {hasCustomFields && (() => {
        // Build displayable fields with proper labels from template
        const displayableFields = Object.entries(preDepartureFields)
          .map(([key, value]) => {
            const label = getFieldLabel(key);
            return label ? { key, label, value } : null;
          })
          .filter(Boolean); // Remove any null entries (fields with ID-like labels)

        return displayableFields.length > 0 && (
          <div className="mt-3 bg-cyan-50 rounded p-2 border border-cyan-100">
            <p className="text-xs font-medium text-cyan-700 mb-2">Pre-Departure Data</p>
            <div className="grid grid-cols-2 gap-2">
              {displayableFields.map((field) => (
                <div key={field.key} className="text-sm">
                  <span className="text-gray-500">{field.label}: </span>
                  <span className="text-gray-700 font-medium">
                    {typeof field.value === 'boolean' ? (field.value ? 'Yes' : 'No') : field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Pre-Departure Notes (from voyage form) */}
      {preDepartureNotes && (
        <div className="mt-3 bg-gray-50 rounded p-2">
          <p className="text-sm text-gray-600 italic">
            {preDepartureNotes}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">Completed by: {completedBy}</span>
      </div>
    </div>
  );
};

export default PreDepartureCompactCard;
